import { Link } from "@tanstack/react-router";

/**
 * Player/guardian feedback sub-tab strip — the nav across the
 * `/feedback` viewer surfaces.
 *
 * Ports the legacy `setFeedbackTab` strip
 * (`js/coaching/feedback.js`). Tab set + order mirror the legacy
 * `VALID_FEEDBACK_TABS`: `playlists · notes · clips · summaries ·
 * development`. `playlists` is the default (no `?tab=` ⇒
 * Playlists), mirroring legacy.
 *
 * Unlike the coach strip, every tab here is a `?tab=` on the
 * single `/feedback` route — there's no separate-route tab like
 * Coach Review.
 */

export type FeedbackTab =
  | "playlists"
  | "notes"
  | "clips"
  | "summaries"
  | "development";

const TABS: ReadonlyArray<{ id: FeedbackTab; label: string }> = [
  { id: "playlists", label: "Playlists" },
  { id: "notes", label: "Notes" },
  { id: "clips", label: "Clips" },
  { id: "summaries", label: "Match Summaries" },
  { id: "development", label: "Development" },
];

export type FeedbackTabStripProps = {
  /** The currently-active tab — the shell computes it from `?tab=`. */
  active: FeedbackTab;
};

export function FeedbackTabStrip({ active }: FeedbackTabStripProps) {
  return (
    <nav
      className="flex gap-1 overflow-x-auto rounded-md border border-rule bg-bg-card p-1"
      role="tablist"
      aria-label="My feedback"
      data-testid="feedback-tab-strip"
    >
      {TABS.map((tab) => {
        const isActive = tab.id === active;
        // Playlists is the default — no `?tab=` param.
        const linkProps =
          tab.id === "playlists"
            ? ({ to: "/feedback", search: {} } as const)
            : ({ to: "/feedback", search: { tab: tab.id } } as const);
        return (
          <Link
            key={tab.id}
            {...linkProps}
            role="tab"
            aria-selected={isActive}
            data-feedback-tab={tab.id}
            data-active={isActive}
            className={[
              "shrink-0 rounded px-3 py-1.5 text-sm font-display uppercase tracking-wide transition-colors",
              isActive
                ? "bg-accent text-white"
                : "text-ink-muted hover:text-ink hover:bg-surface",
            ].join(" ")}
          >
            {tab.label}
          </Link>
        );
      })}
    </nav>
  );
}
